import { isHolidayOrSunday, setSpainRegion } from './holidays';
import { getRate } from '../types/RATES';
import { TIERS } from '../types/TIERS';
import { TipoPago } from '../types/TipoPago';
import { Leg as TripLeg } from '../types/trip';
import { fechaLocalYYYYMMDD, splitIntervalByDay, getTierPercentageForHours } from './datetime';
import { getFestivoNacionalPrice } from './regionHourlyRates';
import {
  PaymentDayBreakdown,
  PaymentCalculationOptions,
  PaymentCalculationResult,
  PaymentDayBreakdown as DiaTrabajo,
} from '../types/payment';
export type {
  PaymentDayBreakdown,
  PaymentCalculationOptions,
  PaymentCalculationResult,
  DiaTrabajo,
  PagoRangoOptions,
  PagoResult,
} from '../types/payment';
import { dayjs, parseIsoToTz } from '../lib/dayjs';

// compatibility re-exports (deprecated Spanish names kept for migration)
// Re-export these from datetime explicitly to avoid duplication in index.ts barrel
export { getDurationHours, formatDateTime } from './datetime';
export { segmentarPorDia, porcentajePorHoras } from './datetime';

export const FESTIVO_PRICE = 25; // flat price for Sundays (unchanged)

/**
 * Calculates the base amount for pacto/empresa tiers for a given hour span.
 * Pure function using TIERS and regional rates; inputs use Europe/Madrid context.
 */
export function calculateTierBaseAmount(horas: number, tipo: TipoPago, region?: string): number {
  const rate = getRate(tipo, region);
  if (rate === undefined) {
    throw new TypeError(`Unknown tipo: ${String(tipo)}`);
  }
  for (const tier of TIERS) {
    if (horas <= tier.max) {
      return rate * tier.percent;
    }
  }
  return 0;
}

/**
 * Splits a trip leg into day-bounded segments and calculates pacto payment per day.
 * Inputs must be ISO strings (`YYYY-MM-DDTHH:mm`, Europe/Madrid). Pure function.
 */
function calculatePactoRangePayment(
  inicioIso: string,
  finIso: string,
  tipo: TipoPago,
  options?: PaymentCalculationOptions
): PaymentCalculationResult {
  const segmentos = splitIntervalByDay(inicioIso, finIso);
  if (options && options.region) {
    setSpainRegion(options.region);
  }
  const festivoPriceOption =
    options && typeof options.festivoPrice === 'number' ? options.festivoPrice : undefined;

  const dias: PaymentDayBreakdown[] = segmentos.map(({ inicio, fin }) => {
    const inicioDayjs = parseIsoToTz(inicio);
    const finDayjs = parseIsoToTz(fin);
    const horas = Math.max(0, finDayjs.diff(inicioDayjs, 'hour', true));
    const porcentaje = getTierPercentageForHours(horas);
    const festivo = isHolidayOrSunday(inicio);
    const pagoBase = calculateTierBaseAmount(horas, tipo, options?.region);
    const fecha = fechaLocalYYYYMMDD(inicio);
    // Sundays receive a flat €25 bonus. National/regional holidays receive a fixed surcharge.
    let festivoAmount = 0;
    if (festivo) {
      const isSunday = inicioDayjs.day() === 0;
      if (isSunday) {
        // Sundays keep the flat price
        festivoAmount = festivoPriceOption ?? FESTIVO_PRICE;
      } else {
        // National/regional holiday: fixed surcharge calculated dynamically based on region
        festivoAmount = getFestivoNacionalPrice(options?.region, inicioDayjs.year());
      }
    }

    return {
      id: `${fecha}-${inicio}`,
      fecha,
      horas,
      porcentaje,
      pagoBase,
      // festivoAmount is the added surcharge for this day (0 if not festivo)
      festivoAmount,
      inicio,
      fin,
      festivo,
      // preserve the tipo used for this day's calculation so callers can show per-day tipos
      tipo,
    };
  });

  // total remains the base pay (pagoBase). Festivo surcharges are returned separately
  const total = dias.reduce((acc, d) => acc + d.pagoBase, 0);

  // totalFestivos: number of unique festivo dates (deduplicated by fecha)
  const festivoDates = new Set(dias.filter((x) => x.festivo).map((x) => x.fecha));
  const totalFestivos = festivoDates.size;
  // totalFestiveAmount: sum festivo supplement once per festivo date
  const festivoAmountsByDate = new Map<string, number>();
  for (const d of dias) {
    if (!d.festivo) {
      continue;
    }
    if (festivoAmountsByDate.has(d.fecha)) {
      continue;
    }
    // use the festivoAmount computed for this entry (calculated with 8h for holidays)
    festivoAmountsByDate.set(d.fecha, d.festivoAmount ?? 0);
  }
  const totalFestiveAmount = Array.from(festivoAmountsByDate.values()).reduce((a, b) => a + b, 0);

  return { total, totalFestivos, totalFestiveAmount, dias };
}

export type Leg = TripLeg & { tipo: TipoPago };

/**
 * Calculates pacto/empresa payment for multiple legs, adjusting tier percentages per day.
 * Inputs must be ISO `YYYY-MM-DDTHH:mm` in Europe/Madrid. Pure function.
 */
export function calculatePactoLegsPayment(
  legs: Leg[],
  options?: PaymentCalculationOptions
): PaymentCalculationResult {
  const allDias: PaymentDayBreakdown[] = [];
  const festivoDateSet = new Set<string>();

  for (const leg of legs) {
    // Ignore legs with zero duration
    if (leg.inicio === leg.fin) {
      continue;
    }
    const res = calculatePactoRangePayment(leg.inicio, leg.fin, leg.tipo, {
      region: leg.region ?? options?.region,
    });
    allDias.push(...res.dias);
    for (const d of res.dias) {
      if (d.festivo) {
        festivoDateSet.add(d.fecha);
      }
    }
  }

  // Sort dias chronologically so per-day adjustments follow chronological order
  allDias.sort((a, b) => {
    const ta = a.inicio ? dayjs(a.inicio).valueOf() : 0;
    const tb = b.inicio ? dayjs(b.inicio).valueOf() : 0;
    return ta - tb;
  });

  // Ensure per-day porcentaje across multiple leg entries does not exceed 1.0.
  // Walk entries grouped by fecha and cap later entries to the remaining percentage.
  const diasByFecha = new Map<string, DiaTrabajo[]>();
  for (const d of allDias) {
    const arr = diasByFecha.get(d.fecha) ?? [];
    arr.push(d);
    diasByFecha.set(d.fecha, arr);
  }

  const adjustedDias: PaymentDayBreakdown[] = [];
  for (const [_fecha, entries] of diasByFecha) {
    let remaining = 1;
    for (const entry of entries) {
      const originalPct =
        typeof entry.porcentaje === 'number'
          ? entry.porcentaje
          : getTierPercentageForHours(entry.horas);
      const assigned = Math.max(0, Math.min(originalPct, remaining));
      if (assigned !== originalPct) {
        // recompute pagoBase using the tipo's base rate multiplied by the assigned porcentaje
        // use getRate from RATES (falls back to default)
        // round to 2 decimals for cents
        const rate = getRate(entry.tipo!, options?.region);
        entry.porcentaje = assigned;
        // For pacto adjustments, round pagoBase to whole euros to avoid overpaying cents across splits
        entry.pagoBase = Math.round(rate * assigned);
      }
      remaining = Math.max(0, remaining - assigned);
      adjustedDias.push(entry);
    }
  }

  // Recompute totals and festivo aggregates from adjusted dias
  const totalFestivos = festivoDateSet.size;
  // Compute total festivo amount by summing unique per-date supplements
  const festivoAmountsByDate = new Map<string, number>();
  for (const d of adjustedDias) {
    if (d.festivo && !festivoAmountsByDate.has(d.fecha)) {
      festivoAmountsByDate.set(d.fecha, d.festivoAmount ?? 0);
    }
  }
  const totalFestiveAmount = Array.from(festivoAmountsByDate.values()).reduce((a, b) => a + b, 0);

  const totalAdjusted = adjustedDias.reduce((acc, d) => acc + (d.pagoBase ?? 0), 0);

  return { total: totalAdjusted, totalFestivos, totalFestiveAmount, dias: adjustedDias };
}

// Backward-compatible aliases while code migrates to clearer names
export const calcularPago = calculateTierBaseAmount;
export const calcularPagoPorLegs = calculatePactoLegsPayment;
